/**
 * Live chat (P8): one Socket.IO connection per signed-in session, as the current site's
 * ChatInboxContext (docs/legacy-audit/API.md §3), without its second socket.
 *
 * - `start(session)` connects with `auth.token` (the JWT, read once per connection like the old site);
 *   a new session (login, logout, another account) closes the previous socket first.
 * - On every `connect` (first one and each automatic reconnection: a new socket id has lost its rooms)
 *   it emits `register` (personal room) and re-joins every remembered `conv:<id>` room.
 * - Server events become domain events (`message`, `messageUpdated`, `conversation`, `typing`…) for the
 *   React layer (useChatLive), which patches the query cache and stops the 3 s polling while `isLive()`.
 *
 * The server accepts a socket without a valid token (unauthenticated) and only says so when a room is
 * refused (`chat:join_denied {reason:'unauthenticated'}`): such a connection does not count as live.
 */
import { io } from 'socket.io-client';
import type { Message } from '../domain';
import { MessageSchema, toMessage } from '../http/mappers/messages';

/** The part of a socket.io-client `Socket` this module uses (a fake one in tests). */
export interface SocketLike {
  on(event: string, listener: (...args: never[]) => void): unknown;
  emit(event: string, ...args: unknown[]): unknown;
  disconnect(): unknown;
}

export interface SocketOptions {
  path: string;
  auth: { token: string };
  transports: string[];
}

export type SocketFactory = (url: string, options: SocketOptions) => SocketLike;

/** What arrives from the conversation rooms, already mapped to the domain. */
export type ChatEvent =
  | { type: 'message'; threadId: string; message: Message; author: 'client' | 'bot' | 'system' | 'person' }
  | { type: 'messageUpdated'; threadId: string }
  | { type: 'conversation'; threadId: string }
  | { type: 'newConversation' }
  | { type: 'unread'; threadId: string; count: number }
  | { type: 'typing'; threadId: string; isTyping: boolean }
  | { type: 'status'; live: boolean };

export interface ChatSession {
  /** Client id (same as the JWT `clientId`): own typing events are ignored. */
  userId: string;
  token: string;
}

export interface ChatLive {
  /** Connects for this session; no-op when already connected for the same user and token. */
  start(session: ChatSession): void;
  /** Closes the connection (logout). Rooms are forgotten. */
  stop(): void;
  /** Joins a conversation room now (if connected) and after every reconnection. */
  join(threadId: string): void;
  /** «Is writing…» for the other side: start, then an automatic stop after a short idle time. */
  typing(threadId: string): void;
  stopTyping(threadId: string): void;
  /** Connected with a session the server accepted: polling can stop. */
  isLive(): boolean;
  /** The other side is typing in this conversation. */
  isTyping(threadId: string): boolean;
  subscribe(listener: (event: ChatEvent) => void): () => void;
  /** Rooms currently remembered (tests, diagnostics). */
  rooms(): string[];
}

export interface ChatLiveOptions {
  /** API origin; empty = the page's own origin (dev proxy). */
  url: string;
  factory?: SocketFactory;
  /** Idle time after the last keystroke before `chat:typing_stop` (legacy: 2 s). */
  typingIdleMs?: number;
  /** Safety expiry of a remote «is typing» (the server stops it after 5 s). */
  typingTtlMs?: number;
}

const defaultFactory: SocketFactory = (url, options) => io(url, options);

interface RawMessageEvent {
  message?: unknown;
  conversationId?: unknown;
}

const idOf = (v: unknown): string => (typeof v === 'string' ? v : v && typeof v === 'object' ? String(v) : '');

export function createChatLive(options: ChatLiveOptions): ChatLive {
  const factory = options.factory ?? defaultFactory;
  const typingIdleMs = options.typingIdleMs ?? 2000;
  const typingTtlMs = options.typingTtlMs ?? 6000;
  const listeners = new Set<(event: ChatEvent) => void>();
  const rooms = new Set<string>();
  /** Remote typing per conversation: user id → expiry timer. */
  const remoteTyping = new Map<string, Map<string, ReturnType<typeof setTimeout>>>();
  /** Own typing: conversation → idle timer. */
  const ownTyping = new Map<string, ReturnType<typeof setTimeout>>();

  let socket: SocketLike | null = null;
  let session: ChatSession | null = null;
  let connected = false;
  let denied = false;

  const emit = (event: ChatEvent) => {
    for (const l of [...listeners]) l(event);
  };
  const live = () => connected && !denied;
  let lastLive = false;
  const publishStatus = () => {
    const now = live();
    if (now !== lastLive) {
      lastLive = now;
      emit({ type: 'status', live: now });
    }
  };

  const clearRemoteTyping = (threadId?: string) => {
    for (const [id, users] of remoteTyping) {
      if (threadId && id !== threadId) continue;
      for (const t of users.values()) clearTimeout(t);
      remoteTyping.delete(id);
    }
  };

  function attach(s: SocketLike, mine: ChatSession) {
    const on = (event: string, fn: (payload: never) => void) =>
      s.on(event, (payload: never) => {
        if (socket === s) fn(payload);
      });

    on('connect', () => {
      connected = true;
      denied = false;
      s.emit('register');
      for (const id of rooms) s.emit('chat:join', { conversationId: id });
      publishStatus();
    });
    on('disconnect', () => {
      connected = false;
      clearRemoteTyping();
      publishStatus();
    });
    on('connect_error', () => {
      connected = false;
      publishStatus();
    });
    on('chat:join_denied', (p: { conversationId?: unknown; reason?: unknown }) => {
      if (p?.reason === 'unauthenticated') {
        denied = true;
        publishStatus();
      } else {
        rooms.delete(idOf(p?.conversationId));
      }
    });
    on('chat:new_message', (p: RawMessageEvent) => {
      const parsed = MessageSchema.safeParse(p?.message);
      const threadId = idOf(p?.conversationId) || (parsed.success ? idOf(parsed.data.conversation) : '');
      if (!parsed.success || !threadId) return;
      const m = parsed.data;
      const author =
        m.senderModel === 'client'
          ? 'client'
          : m.contentType === 'bot_prompt'
            ? 'bot'
            : m.senderModel === 'system' || m.contentType === 'system'
              ? 'system'
              : 'person';
      // A message ends the sender's «is writing».
      if (author === 'person') clearRemoteTyping(threadId);
      emit({ type: 'message', threadId, message: toMessage(m), author });
    });
    on('chat:message_updated', (p: RawMessageEvent) => {
      const threadId = idOf(p?.conversationId);
      if (threadId) emit({ type: 'messageUpdated', threadId });
    });
    on('chat:conversation_updated', (p: { conversation?: { _id?: unknown } }) => {
      const threadId = idOf(p?.conversation?._id);
      if (threadId) emit({ type: 'conversation', threadId });
    });
    on('chat:new_conversation', () => emit({ type: 'newConversation' }));
    on('chat:unread_count', (p: { conversationId?: unknown; count?: unknown }) => {
      const threadId = idOf(p?.conversationId);
      if (threadId) emit({ type: 'unread', threadId, count: Number(p?.count) || 0 });
    });
    on('chat:typing', (p: { conversationId?: unknown; userId?: unknown; isTyping?: unknown }) => {
      const threadId = idOf(p?.conversationId);
      const who = idOf(p?.userId);
      if (!threadId || !who || who === mine.userId) return;
      const users = remoteTyping.get(threadId) ?? new Map<string, ReturnType<typeof setTimeout>>();
      const prev = users.get(who);
      if (prev) clearTimeout(prev);
      if (p?.isTyping) {
        users.set(
          who,
          setTimeout(() => {
            users.delete(who);
            if (!users.size) remoteTyping.delete(threadId);
            emit({ type: 'typing', threadId, isTyping: false });
          }, typingTtlMs),
        );
        remoteTyping.set(threadId, users);
      } else {
        users.delete(who);
        if (!users.size) remoteTyping.delete(threadId);
      }
      emit({ type: 'typing', threadId, isTyping: (remoteTyping.get(threadId)?.size ?? 0) > 0 });
    });
  }

  function stopTyping(threadId: string) {
    const t = ownTyping.get(threadId);
    if (!t) return;
    clearTimeout(t);
    ownTyping.delete(threadId);
    if (socket && connected) socket.emit('chat:typing_stop', { conversationId: threadId });
  }

  function close() {
    for (const t of ownTyping.values()) clearTimeout(t);
    ownTyping.clear();
    clearRemoteTyping();
    const s = socket;
    socket = null;
    connected = false;
    denied = false;
    s?.disconnect();
    publishStatus();
  }

  return {
    start(next) {
      if (socket && session && session.userId === next.userId && session.token === next.token) return;
      close();
      rooms.clear();
      session = next;
      const base = options.url || (typeof window !== 'undefined' ? window.location.origin : '');
      const s = factory(base, { path: '/socket.io', auth: { token: next.token }, transports: ['websocket', 'polling'] });
      socket = s;
      attach(s, next);
    },
    stop() {
      close();
      rooms.clear();
      session = null;
    },
    join(threadId) {
      if (!threadId || rooms.has(threadId)) return;
      rooms.add(threadId);
      if (socket && connected) socket.emit('chat:join', { conversationId: threadId });
    },
    typing(threadId) {
      if (!socket || !connected || !rooms.has(threadId)) return;
      const t = ownTyping.get(threadId);
      if (t) clearTimeout(t);
      else socket.emit('chat:typing_start', { conversationId: threadId });
      ownTyping.set(
        threadId,
        setTimeout(() => stopTyping(threadId), typingIdleMs),
      );
    },
    stopTyping,
    isLive: live,
    isTyping: (threadId) => (remoteTyping.get(threadId)?.size ?? 0) > 0,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    rooms: () => [...rooms],
  };
}
